import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { listAuditEvents } from '../graphql/queries';

const EVENT_TYPES = ['all', 'quote_generate', 'config_save', 'role_change'];
const PAGE_SIZE = 100;

const actorName = (owner) => String(owner || '').split('::').pop();

/**
 * The collapsed payload cell. Role changes (written by the setUserGroup Lambda,
 * CONF-206) are the one event an admin scans the log for by eye, so they read
 * as a sentence; everything else, and anything unparseable, shows the raw
 * payload. The expanded view always shows the raw JSON.
 */
export const summarizePayload = (event) => {
  const raw = event?.payload ?? '';
  if (event?.eventType !== 'role_change') return raw;
  try {
    const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const verb = p.action === 'remove' ? 'removed' : 'added';
    const preposition = p.action === 'remove' ? 'from' : 'to';
    return `${verb} ${p.group} ${preposition} ${p.targetEmail || p.targetUsername}`;
  } catch {
    return raw;
  }
};

export function AuditLogPage() {
  const [searchParams] = useSearchParams();
  const urlType = searchParams.get('eventType');

  const [events, setEvents] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [typeFilter, setTypeFilter] = useState(EVENT_TYPES.includes(urlType) ? urlType : 'all');
  const [actorFilter, setActorFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const loadPage = useCallback(async (token) => {
    const client = generateClient();
    const res = await client.graphql({
      query: listAuditEvents,
      variables: { limit: PAGE_SIZE, ...(token ? { nextToken: token } : {}) },
    });
    return res?.data?.listAuditEvents ?? { items: [], nextToken: null };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await loadPage(null);
        if (!cancelled) {
          setEvents(page.items || []);
          setNextToken(page.nextToken || null);
        }
      } catch (err) {
        console.error('Failed to load audit events:', err);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    setLoadError(false);
    try {
      const page = await loadPage(nextToken);
      setEvents((prev) => [...prev, ...(page.items || [])]);
      setNextToken(page.nextToken || null);
    } catch (err) {
      console.error('Failed to load more audit events:', err);
      setLoadError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Pages from a list scan aren't globally ordered — sort everything loaded so far.
  const shown = [...events]
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
    .filter((e) => typeFilter === 'all' || e.eventType === typeFilter)
    .filter((e) => !actorFilter || actorName(e.owner).toLowerCase().includes(actorFilter.toLowerCase()))
    .filter((e) => !vendorFilter || String(e.vendor || '').toLowerCase().includes(vendorFilter.toLowerCase()));

  if (loading) return <div className="p-4">Loading audit log...</div>;
  if (loadError && events.length === 0) return <div className="p-4 text-red-400">Failed to load audit events</div>;

  return (
    <div>
      <h1 className="text-xl font-bold text-orange-400 mb-4">Audit Log</h1>
      <div className="mb-3 flex flex-wrap gap-3 items-center">
        <label className="text-gray-300">Event type:</label>
        <select
          data-testid="audit-filter-eventType"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-900 text-white border border-gray-700 px-2 py-1"
        >
          {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input
          data-testid="audit-filter-actor"
          placeholder="Filter by actor..."
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          className="bg-gray-900 text-white border border-gray-700 px-2 py-1"
        />
        <input
          data-testid="audit-filter-vendor"
          placeholder="Filter by vendor..."
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          className="bg-gray-900 text-white border border-gray-700 px-2 py-1"
        />
      </div>
      {shown.length === 0 ? (
        <div className="p-4 text-gray-400">No audit events</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-orange-400 text-left">
                <th className="p-2">timestamp</th>
                <th className="p-2">actor</th>
                <th className="p-2">eventType</th>
                <th className="p-2">artifactType</th>
                <th className="p-2">quoteSerial</th>
                <th className="p-2">vendor</th>
                <th className="p-2">payload</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr key={e.id} className="border-t border-gray-800 align-top">
                  <td className="p-2">{e.createdAt}</td>
                  <td className="p-2">{actorName(e.owner)}</td>
                  <td className="p-2">{e.eventType}</td>
                  <td className="p-2">{e.artifactType}</td>
                  <td className="p-2">{e.quoteSerial || ''}</td>
                  <td className="p-2">{e.vendor}</td>
                  <td className="p-2">
                    <button
                      type="button"
                      data-testid={`audit-payload-toggle-${e.id}`}
                      onClick={() => toggleExpanded(e.id)}
                      className="text-left w-full"
                    >
                      {expandedIds.has(e.id) ? (
                        <pre data-testid={`audit-payload-full-${e.id}`} className="whitespace-pre-wrap max-w-lg">{e.payload}</pre>
                      ) : (
                        <span className="block max-w-xs truncate text-gray-400">{summarizePayload(e)}</span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextToken && (
        <div className="mt-3">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="px-3 py-1 rounded border border-gray-600 disabled:opacity-40"
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
      {loadError && events.length > 0 && (
        <div className="p-2 text-red-400">Failed to load more audit events</div>
      )}
    </div>
  );
}

export default AuditLogPage;
